import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Crown, Building2, DollarSign, Users, Activity, ArrowUp, Lightbulb, MessageSquare, FileCheck2, Search, ShieldCheck, Plus, Trash2, RotateCcw, Library } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/format";

export default function MasterDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("orgs");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/master/overview").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  if (data?.error) return <div className="p-10"><div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-2xl p-6"><div className="font-display font-black">Access denied</div><div className="text-sm text-[#1a1a1a]/70 mt-1">This page is reserved for the Master Controller. Your account is <b>{user?.email}</b>.</div></div></div>;
  if (!data) return <div className="p-10 font-mono text-[#1a1a1a]/55">Loading master dashboard…</div>;

  const T = data.totals;
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const filterOrgs = (rows) => !q.trim() ? rows : rows.filter((o) => (o.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <section className="hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden relative">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
            <Crown className="w-3 h-3 text-[#ff5f15]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Master Controller</span>
          </div>
          <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight">Cross-org control room.</h1>
          <p className="mt-2 text-sm text-white/75 max-w-xl">Every hubCMD tenant, every lead, every upsell signal. Logged in as <b>{user?.email}</b>.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/app/master/orgs" data-testid="master-orgs-btn"
              className="btn-pill btn-pill-primary">
              <Building2 className="w-3.5 h-3.5" /> Organizations & pricing
            </Link>
            <Link to="/app/master/library" data-testid="master-library-btn"
              className="btn-pill bg-white text-[#1a1a1a] border border-white/30 hover:bg-white/90">
              <Library className="w-3.5 h-3.5" /> Catalog curation
            </Link>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={Building2} label="Organizations" value={T.organizations} />
        <Kpi icon={Users} label="Active seats" value={T.active_seats} />
        <Kpi icon={DollarSign} label="MRR (estimate)" value={fmt(T.monthly_recurring)} accent />
        <Kpi icon={FileCheck2} label="Leads" value={T.leads_count} />
        <Kpi icon={MessageSquare} label="Feedback" value={T.feedback_count} />
      </div>

      <div className="flex gap-1 flex-wrap border-b border-[#1a1a1a]/10">
        {[
          { k: "orgs", l: "Organizations", i: Building2 },
          { k: "org_control", l: "Org control", i: ShieldCheck },
          { k: "test_send", l: "Test send", i: FileCheck2 },
          { k: "upsells", l: "Upsell signals", i: ArrowUp },
          { k: "leads", l: "Audit-pack leads", i: FileCheck2 },
          { k: "feedback", l: "Feedback", i: MessageSquare },
          { k: "usage", l: "Feature usage", i: Activity },
        ].map(({ k, l, i: Ic }) => (
          <button key={k} data-testid={`master-tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-[12px] uppercase tracking-wider font-bold rounded-t-xl ${tab === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/65 hover:text-[#1a1a1a]"}`}>
            <Ic className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {tab === "orgs" && (
        <div className="surface-card rounded-2xl p-5" data-testid="master-orgs">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// All organizations</div>
            <div className="relative max-w-xs flex-1 ml-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#1a1a1a]/40" />
              <input data-testid="master-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-full pl-9 pr-3 py-1.5 text-sm" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left">
                <tr><th className="py-1">Org</th><th>Created</th><th>Roles (C·A·U)</th><th>Seats</th><th>Idle</th><th>MRR</th><th>Actions/wk</th><th>Audits/wk</th><th>Last active</th><th></th></tr>
              </thead>
              <tbody>
                {filterOrgs(data.orgs).map((o) => (
                  <tr key={o.id} className="border-t border-[#1a1a1a]/05" data-testid={`master-org-${o.id}`}>
                    <td className="py-2 font-bold">{o.name}<div className="text-[10px] font-mono text-[#1a1a1a]/55">{o.display_id}</div></td>
                    <td className="font-mono text-[10px] text-[#1a1a1a]/65 whitespace-nowrap">{formatDate(o.created_at)}</td>
                    <td className="font-mono text-[11px]">{o.counts.controller} · {o.counts.admin} · {o.counts.user}</td>
                    <td>{o.seats_used}/{o.seat_limit}</td>
                    <td className={o.idle_members ? "text-[#ff4500] font-bold" : ""}>{o.idle_members}</td>
                    <td className="font-display font-bold text-[#ff4500]">{fmt(o.mrr_estimate)}</td>
                    <td>{o.actions_last7d}</td>
                    <td>{o.runs_last7d}</td>
                    <td className="font-mono text-[10px] text-[#1a1a1a]/65 whitespace-nowrap">{o.last_active ? formatDate(o.last_active) : "—"}</td>
                    <td>
                      <button data-testid={`master-switch-to-${o.id}`}
                        onClick={async () => {
                          try {
                            await api.post("/master/switch-org", { org_id: o.id });
                            toast.success(`Now viewing as ${o.name}`);
                            window.dispatchEvent(new Event("hubcmd:org-switched"));
                            window.location.href = "/app";
                          } catch (err) { toast.error(err?.response?.data?.detail || "Switch failed"); }
                        }}
                        className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] hover:underline whitespace-nowrap">
                        View as →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "org_control" && <OrgControlPanel />}

      {tab === "upsells" && (
        <div className="surface-card rounded-2xl p-5" data-testid="master-upsells">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Upsell &amp; improvement opportunities</div>
          {data.upsells.length === 0 ? (
            <div className="text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No signals yet. Once tenants accumulate data, we'll flag low-utilization, near-limit seats, and feature gaps.</div>
          ) : (
            <ul className="space-y-2">
              {data.upsells.map((u, i) => (
                <li key={i} className="flex items-start gap-3 bg-[#fff1ea] border border-[#ff4500]/15 rounded-xl px-3 py-2" data-testid={`upsell-${i}`}>
                  <Lightbulb className="w-4 h-4 text-[#ff4500] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display font-bold text-sm">{u.org_name} <span className="text-[10px] font-mono uppercase text-[#1a1a1a]/55 ml-1">{u.type.replace("_", " ")}</span></div>
                    <div className="text-[12px] text-[#1a1a1a]/75">{u.hint}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "leads" && (
        <div className="surface-card rounded-2xl p-5" data-testid="master-leads">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Audit-pack leads · warm prospects</div>
          {data.leads.length === 0 ? <div className="text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No leads yet.</div> : (
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 text-left"><tr><th className="py-1">Name</th><th>Email</th><th>Company</th><th>Role</th><th>Standard</th><th>When</th><th>Notes</th></tr></thead><tbody>
              {data.leads.map((l) => (
                <tr key={l.id} className="border-t border-[#1a1a1a]/05" data-testid={`lead-${l.id}`}>
                  <td className="py-2 font-bold whitespace-nowrap">{l.name}</td>
                  <td><a href={`mailto:${l.email}`} className="text-[#ff4500] underline">{l.email}</a></td>
                  <td>{l.company || "—"}</td>
                  <td className="text-[11px] font-mono">{l.role || "—"}</td>
                  <td className="text-[11px] font-mono uppercase">{l.standard_name}</td>
                  <td className="text-[10px] font-mono text-[#1a1a1a]/65 whitespace-nowrap">{formatDateTime(l.created_at)}</td>
                  <td className="text-[11px] text-[#1a1a1a]/75 max-w-xs truncate">{l.notes || "—"}</td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {tab === "feedback" && (
        <div className="surface-card rounded-2xl p-5" data-testid="master-feedback">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// User feedback · ideas, bugs, questions</div>
          {data.feedback.length === 0 ? <div className="text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No feedback yet.</div> : (
            <ul className="space-y-2">
              {data.feedback.map((f) => (
                <li key={f.id} className="bg-[#f7f5f2] rounded-xl px-3 py-2" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                    <span className="px-2 py-0.5 rounded-full bg-[#1a1a1a] text-white">{f.category}</span>
                    <span className="text-[#1a1a1a]/55">{f.display_id} · {formatDateTime(f.created_at)} · {f.page || "—"}</span>
                    {f.email && <a className="text-[#ff4500] ml-auto" href={`mailto:${f.email}`}>{f.email}</a>}
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{f.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "usage" && (
        <div className="surface-card rounded-2xl p-5" data-testid="master-usage">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-3">// Feature usage · across all orgs</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ["Actions", data.feature_usage.actions_total],
              ["Checklist runs", data.feature_usage.checklist_runs_total],
              ["Meetings", data.feature_usage.meetings_total],
              ["Destinations configured", data.feature_usage.destinations_total],
              ["Dispatches sent", data.feature_usage.dispatches_total],
              ["5-Whys completed", data.feature_usage.five_whys_total],
              ["Effectiveness reviewed", data.feature_usage.effectiveness_reviewed],
            ].map(([l, v]) => (
              <div key={l} className="bg-[#f7f5f2] rounded-xl p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{l}</div>
                <div className="font-display font-black text-2xl mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className={`surface-card rounded-2xl p-4 ${accent ? "ring-2 ring-[#ff4500]/30" : ""}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`font-display font-black text-2xl mt-1 ${accent ? "text-[#ff4500]" : ""}`}>{value}</div>
    </div>
  );
}


function OrgControlPanel() {
  const [orgs, setOrgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", owner_name: "", owner_password: "", seat_limit: 10 });

  const load = () => api.get("/master/orgs").then((r) => setOrgs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const createOrg = async () => {
    if (!form.name || !form.owner_email) return toast.error("Name + owner email required");
    setCreating(true);
    try {
      await api.post("/master/orgs", form);
      toast.success(`Created "${form.name}"`);
      setForm({ name: "", owner_email: "", owner_name: "", owner_password: "", seat_limit: 10 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };
  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"? Members lose access. You can restore it later.`)) return;
    await api.delete(`/master/orgs/${id}`);
    toast.success("Deactivated");
    load();
  };
  const restore = async (id) => {
    await api.post(`/master/orgs/${id}/restore`, {});
    toast.success("Restored");
    load();
  };
  const setSeatLimit = async (id, current) => {
    const val = window.prompt(`Seat limit for this org (current: ${current || 0})`, current || 5);
    if (!val) return;
    try {
      await api.patch(`/master/orgs/${id}`, { seat_limit: Number(val) });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="master-org-control">
      <div className="surface-card rounded-2xl p-5" data-testid="master-create-org">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Create new organization</div>
        <div className="font-display font-black text-xl">Seed a new org (for trials, testing, or paid onboarding)</div>
        <div className="text-[12px] text-[#1a1a1a]/65 mb-3">Creates the owner user if it doesn&apos;t exist and seeds them as Controller of the new org.</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
          <input data-testid="new-org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Org name" className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
          <input data-testid="new-org-email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
            placeholder="Owner email" className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
          <input data-testid="new-org-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
            placeholder="Owner name (optional)" className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
          <input data-testid="new-org-pw" type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
            placeholder="Owner password (new users only)" className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
          <div className="flex gap-1">
            <input data-testid="new-org-seats" type="number" value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: Number(e.target.value) })}
              className="w-20 bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
            <button onClick={createOrg} disabled={creating} data-testid="new-org-submit"
              className="btn-pill btn-pill-primary flex-1 disabled:opacity-50">
              <Plus className="w-3 h-3" /> {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </div>
      <div className="surface-card rounded-2xl p-5" data-testid="master-org-list">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// All organizations</div>
        <div className="font-display font-black text-xl mb-3">Full override · {orgs.length} orgs</div>
        <ul className="divide-y divide-[#1a1a1a]/08">
          {orgs.map((o) => (
            <li key={o.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`master-org-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm truncate">
                  {o.name}
                  {o.deactivated_at && <span className="ml-2 text-[9px] font-mono uppercase tracking-wider bg-[#ff4500] text-white px-1.5 py-0.5 rounded">Deactivated</span>}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 truncate">
                  {o.id.slice(0, 8)} · {o.members} member(s) · seat_limit {o.seat_limit || 0} · owner {o.owner?.email || "—"}
                </div>
              </div>
              <button onClick={() => setSeatLimit(o.id, o.seat_limit)} data-testid={`master-org-seats-${o.id}`}
                className="btn-pill btn-pill-ghost text-[10px]"><Users className="w-3 h-3" /> Seats</button>
              {o.deactivated_at ? (
                <button onClick={() => restore(o.id)} data-testid={`master-org-restore-${o.id}`}
                  className="btn-pill btn-pill-secondary text-[10px]"><RotateCcw className="w-3 h-3" /> Restore</button>
              ) : (
                <button onClick={() => deactivate(o.id, o.name)} data-testid={`master-org-deactivate-${o.id}`}
                  className="btn-pill btn-pill-ghost text-[10px] text-[#ff4500]"><Trash2 className="w-3 h-3" /> Deactivate</button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

